import type { MouseEvent } from 'react'
import { Copy } from 'lucide-react'
import { toast } from 'sonner'
import { Tip } from '@/shared/components'
import { cn } from '@/shared/lib/utils'

const lineClampClass = {
  1: 'line-clamp-1',
  2: 'line-clamp-2',
  3: 'line-clamp-3',
} as const

interface GenerationFailedMessageProps {
  runId?: string
  message?: string
  lines?: keyof typeof lineClampClass
  showReasonPrefix?: boolean
  className?: string
  labelClassName?: string
  messageClassName?: string
  tipSide?: 'top' | 'bottom' | 'left' | 'right'
  tipAlign?: 'start' | 'center' | 'end'
  hideRunIdText?: boolean
  hideMessageInTrigger?: boolean
}

export function GenerationFailedMessage({
  runId,
  message,
  lines = 2,
  showReasonPrefix = true,
  className,
  labelClassName,
  messageClassName,
  tipSide = 'top',
  tipAlign = 'center',
  hideRunIdText = false,
  hideMessageInTrigger = false,
}: GenerationFailedMessageProps) {
  async function handleCopyRunId(event: MouseEvent<HTMLButtonElement>) {
    event.stopPropagation()
    if (!runId) return

    if (!navigator.clipboard?.writeText) {
      toast.error('当前浏览器不支持复制')
      return
    }

    try {
      await navigator.clipboard.writeText(runId)
      toast.success('已复制任务 ID')
    } catch {
      toast.error('复制任务 ID 失败')
    }
  }

  const content = (
    <div className={cn('min-w-0', className)} title={message}>
      <div className={cn('select-none font-medium', labelClassName)}>生成失败</div>
      {message && !hideMessageInTrigger ? (
        <div className={cn('mt-1 whitespace-normal break-all leading-4', lineClampClass[lines], messageClassName)}>
          {showReasonPrefix ? '' : ''}{message}
        </div>
      ) : null}
    </div>
  )

  if (!message) {
    return content
  }

  return (
    <>
      <Tip
        interactive={true}
        content={
          <div 
            tabIndex={-1}
            className="max-h-[200px] overflow-y-auto break-all whitespace-pre-wrap scrollbar-subtle pr-1 select-text cursor-text outline-none"
            onPointerDown={(e) => {
              e.stopPropagation()
              e.nativeEvent.stopPropagation()
            }}
            onMouseDown={(e) => {
              e.stopPropagation()
              e.nativeEvent.stopPropagation()
            }}
            onKeyDown={(e) => {
              e.stopPropagation()
              e.nativeEvent.stopPropagation()
              e.nativeEvent.stopImmediatePropagation()
            }}
          >
            {runId ? (
              <div className="flex items-center gap-1 mb-1">
                <span>ID: {runId}</span>

                <button
                  type="button"
                  className="pointer-events-auto inline-flex h-4 w-4 shrink-0 items-center justify-center rounded text-current opacity-70 hover:bg-white/10 hover:opacity-100"
                  aria-label="复制任务 ID"
                  title="复制任务 ID"
                  onClick={handleCopyRunId}
                >
                  <Copy className="h-3 w-3" />
                </button>

              </div>
            ) : null}
            <div>失败原因：{message}</div>
          </div>
        }
        side={tipSide}
        align={tipAlign}
        delayDuration={200}
        className="w-auto max-w-[345px] bg-[#292929] border border-[#494949] shadow-[0_4px_10px_rgba(0,0,0,0.5)] text-[13px] text-[#D0D0D0] px-3 py-1.5 rounded-md"
        arrowClassName="-translate-y-1/2 bg-[#292929] fill-[#292929] border-r border-b border-[#494949]"
      >
        <div className="pointer-events-auto cursor-pointer">
          {content}
        </div>
      </Tip>
      {!hideRunIdText && runId ? (
        <div className={cn('mt-1 flex items-center justify-center gap-1 whitespace-normal break-all leading-4', lineClampClass[lines], messageClassName)}>
          <span className="min-w-0 break-all">ID: {runId}</span>
          <Tip content="复制任务 ID">
            <button
              type="button"
              className="pointer-events-auto inline-flex h-4 w-4 shrink-0 items-center justify-center rounded text-current opacity-70 hover:bg-white/10 hover:opacity-100"
              aria-label="复制任务 ID"
              onClick={handleCopyRunId}
            >
              <Copy className="h-3 w-3" />
            </button>
          </Tip>
        </div>
      ) : null}
    </>
  )
}
